import type { Store, Unsubscribe } from '@reduxjs/toolkit';
import type { Middleware } from 'redux';

import { assertNonNullable } from '@disy/lib/custom-error';
import { isEqual } from '@disy/lib/is-equal';

import type { Data } from 'ui/data/data';

import type { RootState } from 'cadenza/store/cadenza-store';

/**
 * @callback
 * @param nextState - State after change
 * @param currentState - State before change (current)
 * @param initialExecution - Whether the callback call is the initial call when setting up the observation or
 *                  a subsequent one. It is an explicit parameter, since undefined could be a
 *                  genuine value for the currentState parameter and therefore can not be used to
 *                  identify this moment.
 * @return 'unsubscribe' - Unsubscribe from state changes
 */
export type OnChangeCallback<O> = (
  nextState: O,
  currentState: O | undefined,
  initialExecution: boolean,
) => void | Promise<void> | 'unsubscribe';

/**
 * Calls a callback function whenever there is a state change and also once initially.
 *
 * The callback is called once initially on registration, so that we do not need another code path for the
 * initialization.
 *
 * _**Important:**_ {@link observeStore} callbacks are only for updating the  UI and must not dispatch actions (see
 * caveats: https://redux.js.org/api/store#subscribelistener), while middlewares (@link createObserveMiddleware) may
 * dispatch actions but should not update the UI. In comparison to middlewares, reducers can patch the state only
 * directly but must not dispatch actions. Use middlewares only if it is not possible to use a reducer.
 *
 * @param store - The store to observe
 * @param select - Select a portion of the state.
 * @param onChange - Callback function that will be called with the new and the old state, as well as a hint on whether
 *   it is the initial callback call or a subsequent change
 * @param [compareStateByValue=false] - If true, the result of the selector is compared with the previous value
 *   by nested value and not just equality, using the lodash isEqual() function. Use wisely, as it has potential
 *   performance impact.
 * @return Unsubscribe function. When this function is called (without any params), the
 *   store removes the corresponding callback from its array of listeners.
 */
export function observeStore<S, O>(
  store: Store<S> & { observeStoreCallbackCallStackDepth: number },
  select: (state: S) => O,
  onChange: OnChangeCallback<O>,
  compareStateByValue = false,
): Unsubscribe {
  let currentState: O | undefined; // old state
  let subscribed = true;

  function handleChange(initialExecution: boolean) {
    // When the store gets updated Redux first collects all listeners before calling their callbacks, which each call
    // this handleChange() function. Thus, when an observeStore()'s onChange() callback causes an unsubscription call
    // for another observerStore() call, that other's handleChange() would still get called. This can lead to errors if
    // the assumptions made in the corresponding select() or onChange() callbacks are not valid any longer (e.g.
    // selecting attributes of an already deleted DataView).
    // With the 'subscribed' flag we take care that we do not execute handleChange() functions for ended subscriptions.
    // By doing so we can use context-sensitive selectors without having to worry about the case when their context is
    // stale (e.g. view deleted).
    if (!subscribed) {
      return;
    }

    const nextState = select(store.getState());
    if (initialExecution || hasStateChanged(currentState, nextState, compareStateByValue)) {
      // IMPORTANT: The following statement should be obsolete if we stop triggering actions in observer callbacks.
      // Save the current state and hand that down to the callback, while updating the
      // actual (current) state locally. This avoids the problem that any action dispatched
      // in the stack starting with the observer's callback would use the stalled state
      // (while evaluating hasStateChanged for example) unless we update the currentState
      // before executing the callback.
      const currentStateTmp = currentState;
      currentState = nextState;

      store.observeStoreCallbackCallStackDepth++;
      if (onChange(nextState, currentStateTmp, initialExecution) === 'unsubscribe') {
        unsubscribe();
      }
      store.observeStoreCallbackCallStackDepth--;
    }
  }

  const reduxUnsubscribe = store.subscribe(() => handleChange(false));
  const unsubscribe = () => {
    subscribed = false;
    reduxUnsubscribe();
  };
  handleChange(true);
  return unsubscribe;
}

/**
 * Creates a middleware that calls the given onChange callback whenever the selector return value changes.
 *
 * The callback is called once initially on registration, so that we do not need another code path for the
 * initialization.
 *
 * _**Important:**_ {@link observeStore} callbacks are only for updating the  UI and must not dispatch actions (see
 * caveats: https://redux.js.org/api/store#subscribelistener), while middlewares (@link createObserveMiddleware) may
 * dispatch actions but should not update the UI. In comparison to middlewares, reducers can patch the state only
 * directly but must not dispatch actions. Use middlewares only if it is not possible to use a reducer.
 *
 * @param select - Select a portion of the state.
 * @param onChange - Callback function that will be called with the new and the old state, as well as a hint on whether
 *   it is the initial callback call or a subsequent change
 * @param [compareStateByValue=false] - If true, the result of the selector is compared with the previous value
 *   by nested value and not just equality, using the lodash {@link isEqual} function. Use wisely, as it has potential
 *   performance impact.
 * @return The created middleware to be registered via {@link import('cadenza/store/cadenza-store').addMiddleware}.
 */
export function createObserveMiddleware<S, O>(
  select: (state: S) => O,
  onChange: OnChangeCallback<O>,
  compareStateByValue = false,
): Middleware {
  return (store) => {
    let currentState: O | undefined;

    function handleChange(initialExecution: boolean) {
      const nextState = select(store.getState());
      if (initialExecution || hasStateChanged(currentState, nextState, compareStateByValue)) {
        /** Use tmpState, since this middleware may be triggered again due to action dispatching inside the
         *  {@link onChange} call */
        const currentStateTmp = currentState;
        currentState = nextState;
        onChange(nextState, currentStateTmp, initialExecution);
      }
    }

    handleChange(true); // Initial callback invocation

    return (next) => (action) => {
      const result = next(action);
      handleChange(false);
      return result;
    };
  };
}

function hasStateChanged<S>(oldState: S | undefined, newState: S, compareStateByValue: boolean) {
  if (compareStateByValue) {
    return !isEqual(oldState, newState);
  } else {
    return oldState !== newState;
  }
}

/**
 * Removes store observer using the function returned by {@link observeStore}.
 *
 * @deprecated Use {@link import('@disy/lib/teardown').tearDown} instead.
 * @param unsubscribe - single redux unsubscribe function
 * @example
 * // Free memory by assigning the return value of this function:
 * this._unsubscribeFromStore = unsubscribeStore(this._unsubscribeFromStore);
 */
export function unsubscribeStore(unsubscribe?: Unsubscribe): undefined;
/**
 * Removes store observers using the functions returned by {@link observeStore}.
 *
 * @deprecated Use {@link import('@disy/lib/teardown').tearDown} instead.
 * @param unsubscribe - array of redux unsubscribe functions
 * @example
 * // Free memory by assigning the return value of this function:
 * this._unsubscribeFromStore = unsubscribeStore(this._unsubscribeFromStore);
 */
export function unsubscribeStore(unsubscribe?: Unsubscribe[]): Unsubscribe[];
export function unsubscribeStore(unsubscribe?: Unsubscribe | Unsubscribe[]): undefined | Unsubscribe[] {
  if (Array.isArray(unsubscribe)) {
    unsubscribe.forEach((u) => u());
    return [];
  } else {
    unsubscribe?.();
  }
}

/**
 * Enum for the status of an async operation.
 *
 * @enum {string}
 * @readonly
 */
export const STATUS = {
  PENDING: 'pending',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
} as const;

export type STATUS = (typeof STATUS)[keyof typeof STATUS];

/**
 * Creates complete initial state using the given partial state and the partial state from the Page
 *
 * Note: It is assumed that merging the given state with the page's state will create complete initial state.
 * There is no runtime check.
 *
 * @param slicePrefix - Name of slice of the store
 * @param state - partial state that will be merged with page's state to create complete state
 */
export function createInitialStateFromPageData<Slice extends keyof RootState>(
  slicePrefix: Slice,
  state: Partial<RootState[Slice]> = {},
) {
  const data = document.querySelector<Data<Partial<RootState[Slice]>>>(`[data-redux-slice='${slicePrefix}']`);
  // If you use this utility, the <data> must be there.
  assertNonNullable(data, `No <data> element found for Redux slice "${slicePrefix}"`);
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return { ...state, ...data.value } as RootState[Slice];
}
